import { Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  BuyerLegalType,
  FieldStatusDto,
  OrgDto,
  PersonDto,
} from "../../../../../../autogen/bff-api";
import { displayPhoneNumber } from "../../../../../../common/formatting/displayPhoneNumber";
import { urls } from "../../../../../../urls";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import { FieldErrorBox } from "./FieldErrorBox";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "BuyerLegalTypeSet",
  "OrganizationCityPresent",
  "OrganizationPostCodePresent",
  "BuyerNutsResolvable",
  "PlaceOfPerformanceNutsResolvable",
  "PublicationContactPhonePresent",
] as const satisfies readonly KnownFieldKey[];

interface ContractingAuthoritySectionProps {
  organization: OrgDto;
  buyerLegalType: BuyerLegalType | null | undefined;
  currentEditor: PersonDto;
  responsibleBuyer: PersonDto;
  readinessFor: ReadinessLookup;
}

const formatAddress = (org: OrgDto) => {
  const cityLine = org.postCode && org.city ? `${org.postCode} ${org.city}` : org.city ?? null;
  const parts = [org.street, cityLine].filter((p): p is string => Boolean(p));
  return parts.length > 0 ? parts.join("\n") : null;
};

const formatPerson = (p: PersonDto) => {
  const lines: string[] = [];
  const fullName = [p.firstName, p.lastName].filter(Boolean).join(" ");
  if (fullName) lines.push(fullName);
  if (p.email) lines.push(p.email);
  if (p.phoneNumber) lines.push(displayPhoneNumber(p.phoneNumber));
  return lines.length > 0 ? lines.join("\n") : null;
};

export const ContractingAuthoritySection = ({
  organization,
  buyerLegalType,
  currentEditor,
  responsibleBuyer,
  readinessFor,
}: ContractingAuthoritySectionProps) => {
  const { t } = useTranslation();

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );
  const phoneGate = readinessFor("PublicationContactPhonePresent");

  const address = formatAddress(organization);
  const responsibleLines = formatPerson(responsibleBuyer);
  const responsibleName = [responsibleBuyer.firstName, responsibleBuyer.lastName]
    .filter(Boolean)
    .join(" ");
  const editorIsNotResponsible = currentEditor.id !== responsibleBuyer.id;

  return (
    <SummarySection
      title={t("Contracting authority")}
      editTarget={{
        kind: "external",
        href: urls.organizations.profile.go(
          organization.id,
          urls.organizations.profile.sections.information,
        ),
        label: t("Edit in organization profile") as string,
      }}
      fieldStatuses={fieldStatuses}
    >
      <SummaryFieldGroup>
        <SummaryField
          label={t("Organization name")}
          value={organization.name || <EmptyValue text={t("Not set")} />}
        />
        <SummaryField
          label={t("Org. number")}
          value={organization.organizationNumber || <EmptyValue text={t("Not set")} />}
          span="half"
        />
        <SummaryField
          label={t("Buyer legal type")}
          value={
            buyerLegalType ? t(buyerLegalType) : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("BuyerLegalTypeSet")}
        />
        <SummaryField
          label={t("Address")}
          value={
            address ? (
              <Text whiteSpace="pre-line">{address}</Text>
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          status={readinessFor("OrganizationCityPresent")}
          extraStatuses={[
            readinessFor("OrganizationPostCodePresent"),
            readinessFor("BuyerNutsResolvable"),
            readinessFor("PlaceOfPerformanceNutsResolvable"),
          ]}
        />
        <SummaryField
          label={t("Email")}
          value={organization.contactEmail || <EmptyValue text={t("Not set")} />}
          span="half"
        />
        <SummaryField
          label={t("Phone")}
          value={
            organization.contactPhone ? (
              displayPhoneNumber(organization.contactPhone)
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
          span="half"
        />
        <SummaryField
          label={t("Responsible buyer")}
          value={
            responsibleLines ? (
              <Stack spacing="0">
                <Text whiteSpace="pre-line">{responsibleLines}</Text>
                {editorIsNotResponsible && (
                  <Text fontSize="xs" color="smMuted" fontStyle="italic" mt="1">
                    {t("Doffin notice will show {{responsible}} as contact", {
                      responsible: responsibleName,
                    })}
                  </Text>
                )}
              </Stack>
            ) : (
              <EmptyValue text={t("Not set")} />
            )
          }
        />
      </SummaryFieldGroup>
      {phoneGate?.error && <FieldErrorBox error={phoneGate.error} />}
    </SummarySection>
  );
};
